import getConfig from 'next/config';

type ScribeConfig = {
  backendUrl: string | undefined;
  vercelUrl: string | undefined;
  cookieDomain: string;
  flagsmithId: string | undefined;
  mixpanelToken: string | undefined;
  analyticsProxyUrl: string | undefined;
  edgeExtensionId: string | undefined;
  extensionId: string | undefined;
  frontendUrl: string | undefined;
  suggestionsBackendUrl: string | undefined;
  isInSingleTenantProductionEnv: boolean;
  isSecureEnv: boolean;
  nodeEnv: 'development' | 'staging' | 'production';
  isProduction: boolean;
  sentryDsn: string | undefined;
  environment: string | undefined;
  llmApiRootUrl?: string;
  cortexServiceApiRootUrl?: string;
  googleOauthClientId: string | undefined;
  isCICD?: boolean | undefined;
  debugCookies?: boolean | undefined;
  enableTrackers?: string | undefined;
  otelCollectorOrigin: string | null;
};

const enforceProtocol = (url: string): string => (url.toLowerCase().startsWith('http') ? url : `https://${url}`);

export const getRuntimeEnv = (): ScribeConfig => {
  // This mimics NextJS' "publicRuntimeConfig" and "serverRuntimeConfig" behavior.
  if (typeof document !== 'undefined') {
    const envEl = document.getElementById('__SCRIBE_ENV__');

    if (envEl?.textContent) return JSON.parse(envEl.textContent);
    // We fall back to the logic below so it doesn't break jest tests.
    // It also avoids hard crashes on the client side.
  }
  // IMPORTANT: Bear in mind that these `process.env` values will be exposed to the client side.
  // Watch out for sensitive information, like tokens or passwords.

  const { publicRuntimeConfig } = getConfig() || { publicRuntimeConfig: {} }; // TODO: Remove this along with the old config system.

  const vercelUrl = publicRuntimeConfig.vercelUrl || process.env.VERCEL_URL || process.env.NEXT_PUBLIC_VERCEL_URL;
  const cookieDomain =
    process.env.NGROK_OVERRIDE === 'true'
      ? process.env.NEXT_PUBLIC_NGROK_COOKIE_DOMAIN
      : vercelUrl || publicRuntimeConfig.cookieDomain || process.env.NEXT_PUBLIC_COOKIE_DOMAIN || '';
  const nodeEnv = publicRuntimeConfig.nodeEnv || process.env.NODE_ENV;
  const isProduction = nodeEnv === 'production';
  const isDevelopment = nodeEnv === 'development';
  const cortexServiceApiRootUrl = publicRuntimeConfig.cortexServiceApiRootUrl || process.env.NEXT_PUBLIC_CORTEX_SERVICE_API_ROOT_URL;
  const frontendUrl =
    process.env.NGROK_OVERRIDE === 'true'
      ? process.env.NEXT_PUBLIC_NGROK_CURSIVE_ROOT_URL
      : (publicRuntimeConfig.cursiveRootUrl || (vercelUrl && enforceProtocol(vercelUrl)) || process.env.NEXT_PUBLIC_CURSIVE_ROOT_URL)
          // Always remove trailing slashes so we're always required to add them.
          ?.replace(/\/$/, '');
  const backendUrl =
    process.env.NGROK_OVERRIDE === 'true'
      ? process.env.NEXT_PUBLIC_NGROK_SCRIBE_API_ROOT_URL?.replace(/\/$/, '')
      : (publicRuntimeConfig.scribeApiRootUrl || process.env.NEXT_PUBLIC_SCRIBE_API_ROOT_URL)?.replace(/\/$/, '');
  const suggestionsBackendUrl = (publicRuntimeConfig.suggestionsApiRootUrl || process.env.NEXT_PUBLIC_SUGGESTIONS_API_ROOT_URL)?.replace(
    /\/$/,
    ''
  );

  const otelCollectorOrigin =
    (publicRuntimeConfig.otelCollectorOrigin || process.env.OTEL_COLLECTOR_ORIGIN || '').replace(/\/$/, '') || null;

  return {
    backendUrl,
    vercelUrl,
    cookieDomain,
    cortexServiceApiRootUrl,
    isSecureEnv:
      isProduction ||
      !nodeEnv.includes('dev') ||
      cookieDomain.indexOf('ngrok.io') > 0 || // ngrok.io is for local development.
      isDevelopment, // we need this to be true in development for auth to work in extension iframes
    nodeEnv,
    isCICD: publicRuntimeConfig.isCICD || !!(process.env.CI || process.env.GITHUB_ACTIONS || process.env.GITHUB_ACTION),
    flagsmithId: publicRuntimeConfig.flagsmithClientId || process.env.NEXT_PUBLIC_FLAGSMITH_CLIENT_ID,
    edgeExtensionId: publicRuntimeConfig.edgeExtensionId || process.env.NEXT_PUBLIC_EDGE_EXTENSION_ID,
    extensionId: publicRuntimeConfig.extensionId || process.env.NEXT_PUBLIC_EXTENSION_ID,
    frontendUrl,
    suggestionsBackendUrl,
    isInSingleTenantProductionEnv: isProduction && !!(publicRuntimeConfig.singleTenantName || process.env.NEXT_PUBLIC_SINGLE_TENANT_NAME),
    isProduction,
    environment: publicRuntimeConfig.ENV || process.env.ENV,
    mixpanelToken: publicRuntimeConfig.mixpanelProjectToken || process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN,
    analyticsProxyUrl: publicRuntimeConfig.analyticsProxyUrl || process.env.NEXT_PUBLIC_ANALYTICS_PROXY_URL,
    sentryDsn: publicRuntimeConfig.sentryDsn || process.env.NEXT_PUBLIC_SENTRY_DSN,
    llmApiRootUrl: publicRuntimeConfig.llmApiRootUrl || process.env.NEXT_PUBLIC_LLM_API_ROOT_URL,
    googleOauthClientId: publicRuntimeConfig.googleOauthClientId || process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID,
    debugCookies: process.env.DEBUG_COOKIES === 'true' && isDevelopment,
    enableTrackers: publicRuntimeConfig.enableTrackers || process.env.NEXT_PUBLIC_ENABLE_TRACKERS,
    otelCollectorOrigin
  };
};
